import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../config/settings';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse, mergeSearchFormKeywords } from '../../util/search';
import { withViewport } from '../../util/uiHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  NamedRedirect,
} from '../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import routeConfiguration from '../../routing/routeConfiguration';
import { triggerPromotixAppLogout } from '../../util/promotixCrossLogout';
import {
  consumePostSellerLoginReturnPath,
  peekPostSellerLoginReturnPath,
} from '../../util/postSellerLoginReturnPath';
import { sellerAppAuthCookieName } from '../../util/sellerAppAuthCookie';
import { subscribeOpenBrowseMegaMenu } from '../../util/browseMegaMenuEvents';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  if (!show) {
    return null;
  }
  const classes = classNames(css.genericError, css.genericErrorVisible);
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.clearCookie = this.clearCookie.bind(this);
    this.checkAndCreateUserWithCookies = this.checkAndCreateUserWithCookies.bind(this);
    this.scheduleSellerAppLoginCheck = this.scheduleSellerAppLoginCheck.bind(this);
    this.handleOpenBrowseMenuFromHero = this.handleOpenBrowseMenuFromHero.bind(this);
    this.sellerAppLoginAttempted = false;
    this.sellerAppLoginTimer = null;
  }

  handleOpenBrowseMenuFromHero() {
    if (typeof window !== 'undefined' && window.innerWidth < MAX_MOBILE_SCREEN_WIDTH) {
      this.handleMobileMenuOpen();
    }
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const geoKeys = [
      'address',
      'bounds',
      ...(isOriginInUse(config) ? ['origin'] : []),
    ];

    let searchParams;
    if (isMainSearchTypeKeywords(config)) {
      searchParams = mergeSearchFormKeywords(
        {
          ...currentSearchParams,
          keywords: values?.keywords,
        },
        values
      );
    } else {
      const { search, selectedPlace } = values?.location || {};
      const trimmedSearch = search != null ? String(search).trim() : '';
      if (!trimmedSearch) {
        searchParams = mergeSearchFormKeywords(omit(currentSearchParams, geoKeys), values);
      } else {
        const { origin, bounds } = selectedPlace || {};
        const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
        searchParams = mergeSearchFormKeywords(
          {
            ...currentSearchParams,
            ...originMaybe,
            address: search,
            bounds,
          },
          values
        );
      }
    }
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  clearCookie = (name) => {
    const pastDate = new Date(0);
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const domains = [
      '',
      hostname ? `; domain=${hostname}` : '',
      '; domain=.promotix.com',
    ];
    domains.forEach(domain => {
      document.cookie = `${name}=; expires=${pastDate.toUTCString()}${domain}; path=/`;
    });
  }

//   clearCookie = (name) => {
//     const pastDate = new Date(0); 
//     const domain = "; domain=localhost;";  // Change the domain to localhost
//     document.cookie = name + "=; expires=" + pastDate.toUTCString() + domain + " path=/";
// }

  checkAndCreateUserWithCookies = async (cookieKey) => {
    const cookies = document.cookie;
    const cookieArray = cookies.split(';');
  
    for (let i = 0; i < cookieArray.length; i++) {
      const [cookieName, ...cookieParts] = cookieArray[i].trim().split('=');
      const cookieValue = cookieParts.join('=');
  
      if (cookieName === cookieKey) {
        this.sellerAppLoginAttempted = true;
        const res = await this.props.onLoginWithSellerApp({ auth_token: cookieValue });
        const loginSucceeded =
          res &&
          (res.status === 'SUCCESS' ||
            res.status === 200 ||
            res.statusText === 'OK' ||
            res.statusText === 'Successfully logged In!');
        if (loginSucceeded) {
          this.clearCookie(cookieKey);
          const returnTo = consumePostSellerLoginReturnPath();
          if (returnTo) {
            this.props.history.replace(returnTo);
          } else {
            this.props.history.push(
              createResourceLocatorString('SearchPage', this.props.routeConfiguration, {}, {})
            );
          }
        }
        return; // Exit the loop if the cookie is found
      }
    }

    // The production cookie may be HttpOnly. Staging uses a readable
    // auth_token_stg and must not fall through to the shared auth_token cookie.
    if (cookieKey !== 'auth_token') {
      return;
    }
    this.sellerAppLoginAttempted = true;
    const res = await this.props.onLoginWithSellerApp({});
    const loginSucceeded =
      res &&
      (res.status === 'SUCCESS' ||
        res.status === 200 ||
        res.statusText === 'OK' ||
        res.statusText === 'Successfully logged In!');
    if (loginSucceeded) {
      this.clearCookie(cookieKey);
      const returnTo = consumePostSellerLoginReturnPath();
      if (returnTo) {
        this.props.history.replace(returnTo);
      } else {
        this.props.history.push(
          createResourceLocatorString('SearchPage', this.props.routeConfiguration, {}, {})
        );
      }
    }
  }

  scheduleSellerAppLoginCheck() {
    const { authInfoLoaded } = this.props;
    if (!authInfoLoaded || this.sellerAppLoginAttempted) {
      return;
    }
    if (this.sellerAppLoginTimer) {
      clearTimeout(this.sellerAppLoginTimer);
    }
    const delayMs = peekPostSellerLoginReturnPath() ? 0 : 2000;
    this.sellerAppLoginTimer = setTimeout(() => {
      this.sellerAppLoginTimer = null;
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      this.checkAndCreateUserWithCookies(sellerAppAuthCookieName(hostname));
    }, delayMs);
  }

  componentDidMount() {
    this.unsubscribeOpenBrowseMenu = subscribeOpenBrowseMegaMenu(this.handleOpenBrowseMenuFromHero);
    this.scheduleSellerAppLoginCheck();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.authInfoLoaded !== this.props.authInfoLoaded ||
      prevProps.isAuthenticated !== this.props.isAuthenticated
    ) {
      this.scheduleSellerAppLoginCheck();
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeOpenBrowseMenu) {
      this.unsubscribeOpenBrowseMenu();
    }
    if (this.sellerAppLoginTimer) {
      clearTimeout(this.sellerAppLoginTimer);
      this.sellerAppLoginTimer = null;
    }
  }



  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      this.clearCookie(sellerAppAuthCookieName(hostname));
      triggerPromotixAppLogout();
      const path = pathByRouteName('SearchPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      viewport,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      history,
      onLoginWithSellerApp,
      mobileLogoCentered,
      hideMobileSearch,
    } = this.props;

    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const mobileMenu = (
      <TopbarMobileMenu
        appConfig={config}
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={currentPage}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: keywords || '' };
      }

      // Only hydrate location from URL when full place object is available.
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        keywords: keywords || '',
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : { search: '', selectedPlace: null },
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={currentPage}
        />
   
        <div
          className={classNames(mobileRootClassName || css.container, mobileClassName, {
            [css.containerMapLogoCentered]: mobileLogoCentered,
          })}
        >
          <Button
            rootClassName={css.menu}
            onClick={this.handleMobileMenuOpen}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon className={css.menuIcon} />
            {notificationDot}
          </Button>
          <LinkedLogo
            format={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            className={mobileLogoCentered ? css.centeredLogoSlot : null}
          />
          {hideMobileSearch ? (
            <div className={css.searchMenuPlaceholder} aria-hidden="true" />
          ) : (
            <Button
              rootClassName={css.searchMenu}
              onClick={this.handleMobileSearchOpen}
              title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
            >
              <SearchIcon className={css.searchMenuIcon} />
            </Button>
          )}
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={currentPage}
            location={location}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            appConfig={config}
          />
        
        </div>
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.menuModalContainer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        {hideMobileSearch ? null : (
          <Modal
            id="TopbarMobileSearch"
            containerClassName={css.modalContainer}
            isOpen={isMobileSearchOpen}
            onClose={this.handleMobileSearchClose}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.searchContainer}>
              <TopbarSearchForm
                onSubmit={this.handleSubmit}
                initialValues={initialSearchFormValues}
                isMobile
                appConfig={config}
              />
              <p className={css.mobileHelp}>
                <FormattedMessage id="Topbar.mobileSearchHelp" />
              </p>
            </div>
          </Modal>
        )}
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  mobileLogoCentered: false,
  hideMobileSearch: false,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  mobileLogoCentered: bool,
  hideMobileSearch: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedTopbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

const Topbar = withViewport(EnhancedTopbar);
Topbar.displayName = 'Topbar';

export default Topbar;
